import { useId, useRef, useState } from "react";
import { palettesForSkin, paletteMode, resolvePaletteForSkin } from "../appearance/registry";
import { SKINS } from "../appearance/skins";
import type { AppearanceState, SkinEntry } from "../appearance/types";
import type { AppLocale } from "../i18n/format";

/**
 * The Themes gallery.
 *
 * Each card holds a LIVE MINIATURE of the real interface: the same class names
 * as the Convert screen, inheriting the same palette/form/layout CSS scoped to
 * the card by three data attributes. Only possible because the appearance
 * stylesheets are written against bare attribute selectors rather than
 * `:root[...]`. Swatches would tell you the colours but not that a form squares
 * the corners or a layout collapses the rows.
 *
 * ACCESSIBILITY — the nesting problem and how it is solved.
 * Interactive content is not permitted inside role="radio", so the palette dots
 * cannot live inside the skin's radio. Instead there are TWO separate
 * roving-tabindex groups:
 *
 *   outer  role="radiogroup"  the skin cards; each card's main button is a radio
 *   inner  role="radiogroup"  that card's palette dots, a SIBLING of the radio
 *
 * Arrow keys act on whichever group holds focus, and Tab moves between the two
 * levels. No key is intercepted, nothing is trapped, and neither group needs to
 * know the other exists.
 */

interface Props {
  readonly locale: AppLocale;
  readonly appearance: AppearanceState;
  readonly onApply: (skin: SkinEntry, paletteId: string) => void;
}

function CheckGlyph({ className }: { readonly className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 8.5 6.5 12 13 4.5" />
    </svg>
  );
}

function DiceGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="2.25" y="2.25" width="11.5" height="11.5" rx="2" />
      <path d="M5.5 5.5h.01M10.5 5.5h.01M8 8h.01M5.5 10.5h.01M10.5 10.5h.01" />
    </svg>
  );
}

/** A miniature of the Convert screen using the real markup and class names. */
function MiniPreview({ locale }: { readonly locale: AppLocale }) {
  const ar = locale === "ar";
  const rows = [
    { code: "SAR", flag: "sa", name: ar ? "الريال السعودي" : "Saudi Riyal", amount: "1,250", base: true },
    { code: "USD", flag: "us", name: ar ? "الدولار الأمريكي" : "US Dollar", amount: "333.33", base: false },
    { code: "EUR", flag: "eu", name: ar ? "اليورو" : "Euro", amount: "288.71", base: false },
  ];

  return (
    <div className="mini" aria-hidden="true">
      <div className="mini__status">
        <span className="mini__gauge" />
        <span className="mini__meta">{ar ? "الأسعار بتاريخ 09 أغسطس" : "Rates as of 09 Aug"}</span>
      </div>
      <div className="rate-table">
        <div className="rate-table__body">
          {rows.map((row) => (
            <div className="row" key={row.code} data-base={row.base ? "true" : undefined}>
              <div className="row__id">
                <img className="row__flag" src={`/flags/${row.flag}.svg`} alt="" />
                <span className="row__identity">
                  <span className="row__code-line">
                    <span className="row__code">{row.code}</span>
                  </span>
                  <span className="row__name">{row.name}</span>
                </span>
              </div>
              <div className="row__value">
                <span className="row__amount" dir="ltr">
                  {row.amount}
                </span>
                {row.base ? null : (
                  <span className="row__unit" dir="ltr">
                    1 SAR = 0.2667
                  </span>
                )}
              </div>
              <div className="row__action">
                <span className="mini__dots" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SkinCard({
  skin,
  locale,
  isActive,
  activePalette,
  tabbable,
  onApply,
  onFocusCard,
  registerRef,
}: {
  readonly skin: SkinEntry;
  readonly locale: AppLocale;
  readonly isActive: boolean;
  readonly activePalette: string;
  readonly tabbable: boolean;
  readonly onApply: (paletteId: string) => void;
  readonly onFocusCard: () => void;
  readonly registerRef: (el: HTMLButtonElement | null) => void;
}) {
  const ar = locale === "ar";
  const offered = palettesForSkin(skin.id);
  const labelId = useId();

  /*
   * The palette shown in THIS card's miniature. It follows the applied palette
   * when the card is the active one, and otherwise shows the skin's own
   * default, so every card presents itself as its author intended.
   */
  const shown = isActive ? activePalette : resolvePaletteForSkin(skin.defaultPalette, skin.id);

  const dotRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const onDotKeyDown = (event: React.KeyboardEvent, index: number) => {
    const forward = ar ? "ArrowLeft" : "ArrowRight";
    const back = ar ? "ArrowRight" : "ArrowLeft";
    let next = -1;
    if (event.key === forward || event.key === "ArrowDown") next = index + 1;
    else if (event.key === back || event.key === "ArrowUp") next = index - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = offered.length - 1;
    else return;

    event.preventDefault();
    event.stopPropagation();
    const clamped = Math.max(0, Math.min(offered.length - 1, next));
    dotRefs.current[clamped]?.focus();
  };

  return (
    <li className="skin-card__item">
      <div className="skin-card" data-active={isActive ? "true" : undefined}>
        <button
          type="button"
          className="skin-card__choice"
          role="radio"
          aria-checked={isActive}
          aria-labelledby={labelId}
          tabIndex={tabbable ? 0 : -1}
          ref={registerRef}
          onFocus={onFocusCard}
          onClick={() => onApply(shown)}
        >
          <span
            className="skin-card__preview"
            data-palette={shown}
            data-form={skin.form}
            data-layout={skin.layout}
          >
            <MiniPreview locale={locale} />
          </span>

          <span className="skin-card__body">
            <span className="skin-card__head">
              <span className="skin-card__name" id={labelId}>
                {ar ? skin.nameAr : skin.nameEn}
              </span>
              <span className="skin-card__badge" data-mode={paletteMode(shown)}>
                {paletteMode(shown) === "dark" ? (ar ? "داكن" : "dark") : ar ? "فاتح" : "light"}
              </span>
            </span>
            <span className="skin-card__desc">{ar ? skin.descAr : skin.descEn}</span>
          </span>

          {isActive ? (
            <span className="skin-card__selected">
              <CheckGlyph />
              <span className="visually-hidden">{ar ? "المختار" : "Selected"}</span>
            </span>
          ) : null}
        </button>

        {/*
         * Sibling of the radio, never a child of it. A second roving-tabindex
         * group with its own arrow handling — see the note at the top.
         */}
        <div
          className="dots"
          role="radiogroup"
          aria-label={
            ar
              ? `لوحة ألوان ${skin.nameAr}`
              : `Palette for ${skin.nameEn}`
          }
        >
          {offered.map((option, index) => {
            const selected = isActive && option.entry.id === activePalette;
            const name = ar ? option.entry.nameAr : option.entry.nameEn;
            return (
              <button
                key={option.entry.id}
                type="button"
                role="radio"
                aria-checked={selected}
                /* Never "colour 3": the name says what it is, and whether it
                   was authored for this skin in particular. */
                aria-label={
                  option.specific
                    ? ar
                      ? `${name} — خاصة بـ${skin.nameAr}`
                      : `${name} — specific to ${skin.nameEn}`
                    : name
                }
                title={name}
                className="dots__dot"
                data-specific={option.specific ? "true" : undefined}
                data-palette={option.entry.id}
                tabIndex={index === 0 ? 0 : -1}
                ref={(el) => {
                  dotRefs.current[index] = el;
                }}
                onKeyDown={(event) => onDotKeyDown(event, index)}
                onClick={() => onApply(option.entry.id)}
              >
                <span className="dots__swatch" aria-hidden="true" />
                {selected ? <CheckGlyph className="dots__check" /> : null}
              </button>
            );
          })}
        </div>
      </div>
    </li>
  );
}

export function ThemesScreen({ locale, appearance, onApply }: Props) {
  const ar = locale === "ar";
  const activeSkin = SKINS.find(
    (s) => s.form === appearance.form && s.layout === appearance.layout,
  );

  const [focusedIndex, setFocusedIndex] = useState(() => {
    const i = SKINS.findIndex((s) => s.id === activeSkin?.id);
    return i >= 0 ? i : 0;
  });
  const cardRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const onGridKeyDown = (event: React.KeyboardEvent) => {
    const forward = ar ? "ArrowLeft" : "ArrowRight";
    const back = ar ? "ArrowRight" : "ArrowLeft";
    let next = -1;
    if (event.key === forward || event.key === "ArrowDown") next = focusedIndex + 1;
    else if (event.key === back || event.key === "ArrowUp") next = focusedIndex - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = SKINS.length - 1;
    else return;

    event.preventDefault();
    const clamped = Math.max(0, Math.min(SKINS.length - 1, next));
    setFocusedIndex(clamped);
    cardRefs.current[clamped]?.focus();
  };

  /** Random skin + random compatible palette, never the current combination. */
  const surprise = () => {
    const combos: Array<{ skin: SkinEntry; palette: string }> = [];
    for (const skin of SKINS) {
      for (const { entry } of palettesForSkin(skin.id)) {
        const sameSkin = skin.form === appearance.form && skin.layout === appearance.layout;
        if (sameSkin && entry.id === appearance.palette) continue;
        combos.push({ skin, palette: entry.id });
      }
    }
    const pick = combos[Math.floor(Math.random() * combos.length)];
    if (pick) onApply(pick.skin, pick.palette);
  };

  return (
    <main className="screen screen--themes">
      <header className="screen__head">
        <h1 className="screen__title">{ar ? "ثيمات" : "Themes"}</h1>
        <p className="screen__sub">
          {ar
            ? "اختر مظهراً كاملاً — يتغيّر كل شيء معاً."
            : "Pick a complete look — everything changes together."}
        </p>
      </header>

      <ul
        className="skin-grid"
        role="radiogroup"
        aria-label={ar ? "المظاهر" : "Skins"}
        onKeyDown={onGridKeyDown}
      >
        {SKINS.map((skin, index) => (
          <SkinCard
            key={skin.id}
            skin={skin}
            locale={locale}
            isActive={activeSkin?.id === skin.id}
            activePalette={appearance.palette}
            tabbable={index === focusedIndex}
            onFocusCard={() => setFocusedIndex(index)}
            registerRef={(el) => {
              cardRefs.current[index] = el;
            }}
            onApply={(paletteId) => onApply(skin, paletteId)}
          />
        ))}
      </ul>

      <button type="button" className="surprise" onClick={surprise}>
        <DiceGlyph />
        <span>{ar ? "فاجئني" : "Surprise me"}</span>
      </button>
    </main>
  );
}
